import { useMemo, useState } from 'react';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import {
  NeedDto,
  ResourceLocationDto,
  ScheduledEventDto,
  UserNeedDto,
  VisitDto,
} from '@act/shared/data-transfer-objects';
import { FormBuilder, FlexContainer } from '@act/storybook';
import { TrackingProvider } from 'react-components/tracking';
import CompositeDialog from '../dialog/CompositeDialog';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { HospitalizationFollowUpCheckboxSection } from './HospitalizationFollowUpCheckboxSection';
import {
  HOSPITALIZATION_FOLLOWUP_VISIT_CHECKBOX_INFO_MESSAGE,
  HOSPITALIZATION_FOLLOWUP_VISIT_CHECKBOX_LABEL,
} from './hospitalizationFollowUpOutcome.util';
import { useVisitDialog } from './hooks/useVisitDialog.hook';
import { buildVisitFormConfig } from './visitDialogFormConfig.util';
import { DialogTitleWithRecoveredNote } from './components/DialogTitleWithRecoveredNote';

export const enum VisitMode {
  CREATE = 'Create',
  EDIT = 'Edit',
}

export type ResourceCommunicationDetails = {
  title: string;
  description: string;
  findHelpId: string;
  location: ResourceLocationDto;
  need?: UserNeedDto;
  isCustomResourcing?: boolean;
};

export type CreateVisitData = {
  mode: VisitMode.CREATE;
  resource?: ResourceCommunicationDetails;
  scheduledEventId?: number;
};

export type EditVisitData = {
  mode: VisitMode.EDIT;
  visit: VisitDto;
};

export interface VisitDialogInterfaceProps {
  userId: string;
  visit?: VisitDto;
  resourceDetails?: ResourceCommunicationDetails;
  scheduledEvent?: ScheduledEventDto;
  userNeed?: UserNeedDto;
  isCustomResourcing?: boolean;
  need?: NeedDto;
  fromResourceHub?: boolean;
  completionCallback?: () => void;
}

export type VisitDialogData = CreateVisitData | EditVisitData;

interface VisitDialogProps extends VisitDialogInterfaceProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit';
  open: boolean;
}

export interface VisitForm {
  date: string;
  duration: number;
  addressId: number;
  location: string;
  note: string;
  outcomeId: number;
  start: number;
  resourceName: string;
  userNeedId: number;
  needCategory: number;
  needStatus: string;
  needTitle: string;
}

/**
 * Dialog for creating or editing a member visit.
 * @param props Dialog open state, member id, optional visit, and close handler.
 * @returns Visit create/edit dialog UI.
 */
const VisitDialog = ({
  open,
  scheduledEvent,
  handleClose,
  userId,
  visit,
  mode,
  completionCallback,
}: VisitDialogProps) => {
  const [showRecoveredNote, setShowRecoveredNote] = useState(false);

  const {
    actions,
    addressId,
    addressOptions,
    canDelete,
    canEditEngagement,
    confirmDeleteOpen,
    control,
    dateOptions,
    formState,
    handleDeleteVisit,
    handleFormClose,
    handleSave,
    handleSubmit,
    isHospitalizationFollowUpEnabled,
    noteFieldRef,
    outcomeHasCharacterMinimum,
    outcomeOptions,
    setConfirmDeleteOpen,
    showHospitalizationFollowUpCheckbox,
    startTimeOptions,
    title,
    visitNoteSaveOverride,
  } = useVisitDialog({
    scheduledEvent,
    handleClose,
    userId,
    visit,
    mode,
    completionCallback,
  });

  const hospitalizationFollowUpFields = useMemo(
    () =>
      showHospitalizationFollowUpCheckbox
        ? [
            {
              type: 'passthrough' as const,
              content: (
                <HospitalizationFollowUpCheckboxSection
                  control={control}
                  infoMessage={
                    HOSPITALIZATION_FOLLOWUP_VISIT_CHECKBOX_INFO_MESSAGE
                  }
                  checkboxLabel={HOSPITALIZATION_FOLLOWUP_VISIT_CHECKBOX_LABEL}
                  disabled={!isHospitalizationFollowUpEnabled}
                  checkboxTrackingName="visit-hospitalization-follow-up-checkbox"
                />
              ),
            },
          ]
        : [],
    [
      control,
      isHospitalizationFollowUpEnabled,
      showHospitalizationFollowUpCheckbox,
    ]
  );

  const formConfig = useMemo(
    () =>
      buildVisitFormConfig({
        dateOptions,
        startTimeOptions,
        addressOptions,
        outcomeOptions,
        addressId,
        canEditEngagement,
        characterMinimum: outcomeHasCharacterMinimum ? 50 : undefined,
        noteMode: mode === VisitMode.EDIT ? 'edit' : 'create',
        visitId: visit?.id,
        noteId: visit?.noteId,
        userId,
        noteFieldRef,
        noteSave: visitNoteSaveOverride,
        onRecoveredNoteChange: setShowRecoveredNote,
        hospitalizationFollowUpFields,
      }),
    [
      addressId,
      addressOptions,
      canEditEngagement,
      dateOptions,
      hospitalizationFollowUpFields,
      noteFieldRef,
      outcomeHasCharacterMinimum,
      outcomeOptions,
      startTimeOptions,
      mode,
      visit?.id,
      visit?.noteId,
      userId,
      visitNoteSaveOverride,
    ]
  );

  const dialogTitle = useMemo(
    () => (
      <DialogTitleWithRecoveredNote
        title={title}
        showRecoveredNote={showRecoveredNote}
      />
    ),
    [title, showRecoveredNote]
  );

  return (
    <TrackingProvider contextName="visit-dialog">
      <CompositeDialog
        handleClose={handleFormClose}
        maxWidth="md"
        open={open}
        title={dialogTitle}
        icon={
          <FlexContainer
            direction="row"
            margin="0 8px 0 0"
            width="auto"
            height="auto"
            alignItems="center"
          >
            <BusinessOutlinedIcon color="primary" />
          </FlexContainer>
        }
        handleFormSubmit={handleSubmit(data => {
          void handleSave(data);
        })}
        actions={actions}
      >
        <FormBuilder
          config={formConfig}
          control={control}
          formState={formState}
        />
        {canDelete && (
          <ConfirmDeleteDialog
            open={confirmDeleteOpen}
            deleteHandler={() => handleDeleteVisit()}
            handleClose={() => setConfirmDeleteOpen(false)}
            subject={'visit'}
          />
        )}
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default VisitDialog;
